import React from 'react';
import {
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Chip,
  Typography,
  Grid,
  Box,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import SendIcon from '@mui/icons-material/Send';
import type { GradeLevel, Standard } from './mathWordProblemsTypes';

export interface MathProblemsFormProps {
  handleSubmit: (e: React.FormEvent) => void;
  gradeLevel: string;
  setGradeLevel: (value: string) => void;
  availableGradeLevels: string[];
  gradeLevels: GradeLevel[];
  selectedStrand: string;
  setSelectedStrand: (value: string) => void;
  setSelectedStandards: (ids: string[]) => void;
  availableStrands: string[];
  strandFilteredStandards: Standard[];
  selectedStandards: string[];
  standardLoading: boolean;
  numQuestions: number;
  setNumQuestions: (value: number) => void;
  language: 'en' | 'es';
  setLanguage: (value: 'en' | 'es') => void;
  questionStyle: string;
  setQuestionStyle: (value: string) => void;
  questionMix: string;
  setQuestionMix: (value: string) => void;
  isStreaming: boolean;
  isLoading: boolean;
  handleCancelGeneration: () => void;
}

const MathProblemsForm: React.FC<MathProblemsFormProps> = ({
  handleSubmit,
  gradeLevel,
  setGradeLevel,
  availableGradeLevels,
  gradeLevels,
  selectedStrand,
  setSelectedStrand,
  setSelectedStandards,
  availableStrands,
  strandFilteredStandards,
  selectedStandards,
  standardLoading,
  numQuestions,
  setNumQuestions,
  language,
  setLanguage,
  questionStyle,
  setQuestionStyle,
  questionMix,
  setQuestionMix,
  isStreaming,
  isLoading,
  handleCancelGeneration,
}) => (
  <form onSubmit={handleSubmit}>
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Box sx={{ bgcolor: 'grey.50', border: '1px solid #e5e7eb', borderRadius: 2, p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#9c27b0', mb: 2 }}>
            Step 1: Grade & Standards
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Grade Level</InputLabel>
                <Select
                  value={gradeLevel}
                  onChange={(e: SelectChangeEvent) => setGradeLevel(e.target.value)}
                  label="Grade Level"
                >
                  {(availableGradeLevels.length > 0 ? availableGradeLevels : gradeLevels).map((grade) => (
                    <MenuItem key={grade} value={grade}>
                      {grade === 'K' ? 'Kindergarten' : `Grade ${grade}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Strand</InputLabel>
                <Select
                  value={selectedStrand}
                  onChange={(e: SelectChangeEvent) => {
                    setSelectedStrand(e.target.value);
                    setSelectedStandards([]);
                  }}
                  label="Strand"
                >
                  <MenuItem value="All Strands">All Strands</MenuItem>
                  {availableStrands.map((strand) => (
                    <MenuItem key={strand} value={strand}>
                      {strand}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={strandFilteredStandards}
                getOptionLabel={(option) => {
                  const description = option.short_description || option.description || '';
                  const capitalizedDescription = description.charAt(0).toUpperCase() + description.slice(1);
                  return `${option.code || option.standard}: ${capitalizedDescription}`;
                }}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                value={strandFilteredStandards.filter((s) => selectedStandards.includes(s._id))}
                onChange={(_event, newValue) => {
                  setSelectedStandards(newValue.map((s) => s._id));
                }}
                disableCloseOnSelect
                fullWidth
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Learning Standards *"
                    placeholder={selectedStandards.length === 0 ? 'Search for standards...' : ''}
                    size="small"
                    required={selectedStandards.length === 0}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {standardLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option._id}
                      label={option.code || option.standard}
                      size="small"
                      sx={{ fontSize: '0.7rem', height: '20px' }}
                    />
                  ))
                }
              />
            </Grid>
          </Grid>
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        <Box sx={{ bgcolor: 'grey.50', border: '1px solid #e5e7eb', borderRadius: 2, p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#9c27b0', mb: 2 }}>
            Step 2: Problem Options
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                type="number"
                value={numQuestions}
                onChange={(e) => setNumQuestions(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 5)))}
                label="Number of Questions"
                size="small"
                fullWidth
                inputProps={{ min: 1, max: 20 }}
                helperText="1-20 questions"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Language"
                value={language}
                onChange={(e) => setLanguage(e.target.value as 'en' | 'es')}
                SelectProps={{ native: true }}
                size="small"
              >
                <option value="en">English</option>
                <option value="es">Spanish (Beta)</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Question Style</InputLabel>
                <Select
                  value={questionStyle}
                  onChange={(e: SelectChangeEvent) => setQuestionStyle(e.target.value)}
                  label="Question Style"
                >
                  <MenuItem value="StandardizedTest">Standardized Test Format</MenuItem>
                  <MenuItem value="Generic">General Format</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Question Types</InputLabel>
                <Select
                  value={questionMix}
                  onChange={(e: SelectChangeEvent) => setQuestionMix(e.target.value)}
                  label="Question Types"
                >
                  <MenuItem value="multiple_choice">Multiple Choice Only</MenuItem>
                  <MenuItem value="mixed">Multiple Choice + Open Ended</MenuItem>
                  <MenuItem value="text_only">Text Questions Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
          {isStreaming ? (
            <Button
              variant="outlined"
              color="error"
              onClick={handleCancelGeneration}
              size="large"
              sx={{ fontSize: '0.95rem', fontWeight: 700, py: 1.5, px: 4, minWidth: 220, borderRadius: 2 }}
            >
              Cancel Generation
            </Button>
          ) : (
            <Button
              type="submit"
              variant="contained"
              disabled={isStreaming || isLoading || selectedStandards.length === 0}
              startIcon={isLoading ? <CircularProgress size={18} /> : <SendIcon />}
              size="large"
              sx={{
                fontSize: '0.95rem',
                fontWeight: 700,
                py: 1.5,
                px: 4,
                minWidth: 220,
                borderRadius: 2,
                bgcolor: 'secondary.main',
                '&:hover': { bgcolor: 'secondary.dark' },
              }}
            >
              {isLoading ? 'Generating...' : 'Generate Problems'}
            </Button>
          )}
        </Box>
      </Grid>
    </Grid>
  </form>
);

export default MathProblemsForm;
